import React, { FocusEvent } from 'react';
import { useSelector } from 'react-redux';
import { FieldInputProps, FormikErrors } from 'formik';
import { Button, FieldWrapper, FormError, Input, Modal, ModalAvatar, ModalFooter, ModalForm, ModalFormLayout, ModalHeader, ModalTitle, Select } from 'components';
import { ICreatableSelectOption } from 'components/CreatableSelect/CreatableSelect';
import { JobTitleCreatableSelect } from 'components/JobTitleCreatableSelect/JobTitleCreatableSelect';
import { DisbursementOptions, SiteRoleOptions, UserRoleOptions } from 'config/const';
import { Appearance } from 'design/types';
import { selectEmployee } from 'store/Employee';
import { selectCurrentEmployeeOrg } from 'store/Employers';
import { IJobTitleState } from 'store/types';
import { EmployeeActionRequiredType, EmployeeStatus, EmployeeType, IApiErrorPayloadWithCode, IEmployerSite, ISite } from 'types';
import { getDefaultDisbursementMethod, isEmployeeIdRequired } from 'utils';
import { HeaderContent, InvitationNotice, ReviewNowButton, StyledErrorSection } from './styles';

interface ModalProps {
  actionRequired?: EmployeeActionRequiredType;
  apiErrors: IApiErrorPayloadWithCode;
  avatarUrl?: string;
  dirty: boolean;
  errors: FormikErrors<{
    site?: {
      label: string;
      value: string;
    };
    role?: {
      label: string;
      value: string;
    };
    jobTitle?: ICreatableSelectOption<IJobTitleState>;
    employeeId?: string;
  }>;
  fullName: string;
  getFieldProps: (field: string) => FieldInputProps<any>;
  handleBlur?: {
    (e: FocusEvent<any>): void;
    <T = any>(fieldOrEvent: T): T extends string ? (e: any) => void : void;
  };
  hasPaymentMethod: boolean;
  invitationStatus?: EmployeeStatus;
  isActive: boolean;
  isLoading: boolean;
  isSiteRole: boolean;
  isValid: boolean;
  onClose: () => void;
  onSubmit: () => void;
  setFieldTouched: (field: 'role' | 'job' | 'employeeId' | 'site' | 'disbursementMethod', isTouched?: boolean, shouldValidate?: boolean) => void;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  showReviewModal?: () => void;
  sites?: IEmployerSite[];
  sitesWithSettings?: ISite[];
  validateField: (field: 'role' | 'jobTitle' | 'employeeId' | 'site' | 'disbursementMethod') => boolean;
  values: Record<string, any>;
}

const AddUserToSite: React.FC<ModalProps> = ({
  actionRequired,
  apiErrors,
  avatarUrl,
  dirty,
  errors,
  fullName,
  getFieldProps,
  handleBlur,
  hasPaymentMethod,
  invitationStatus,
  isActive,
  isLoading,
  isSiteRole,
  isValid,
  onClose,
  onSubmit,
  setFieldTouched,
  setFieldValue,
  showReviewModal,
  sites,
  sitesWithSettings,
  validateField,
  values,
}) => {
  const { sites: currentSites } = useSelector(selectEmployee()) || {};

  const siteOptions = sites
    ? sites
        .filter((site) => {
          const currentSite = currentSites?.find((cs) => cs.id === site.id);
          return !(currentSite?.status === 'dismissed');
        })
        .map((site) => ({
          label: site.name,
          value: site.id,
        }))
    : [];
  const isPending = invitationStatus === EmployeeStatus.INVITED;
  const isExpired = invitationStatus === EmployeeStatus.ACTION_REQUIRED && actionRequired === EmployeeActionRequiredType.INVITE_EXPIRED;
  const shouldDisplayInviteMethod = isPending || isExpired;
  const isPendingReview = actionRequired === EmployeeActionRequiredType.EMPLOYER_APPROVAL && invitationStatus === EmployeeStatus.ACTION_REQUIRED;
  const currentEmployeeOrg = useSelector(selectCurrentEmployeeOrg);

  return (
    <Modal maxWidth={540} isActive={isActive} onClose={onClose}>
      <ModalHeader>
        <ModalAvatar image={avatarUrl} username={fullName} hasPaymentMethod={hasPaymentMethod} />
        <HeaderContent>
          <ModalTitle>{fullName}</ModalTitle>
        </HeaderContent>
      </ModalHeader>

      {shouldDisplayInviteMethod && currentEmployeeOrg && <InvitationNotice appearance={Appearance.WARNING} message={`Invitation to ${currentEmployeeOrg.name} is ${isPending ? 'pending' : 'expired'}.`} />}

      {isPendingReview ? (
        <InvitationNotice
          appearance={Appearance.WARNING}
          message={
            <>
              <p>User is pending review.</p>
              <ReviewNowButton label="Review Now" onClick={showReviewModal} />.
            </>
          }
        />
      ) : null}

      <ModalForm onSubmit={onSubmit}>
        <ModalFormLayout>
          <FieldWrapper label="Role" message={validateField('role') ? errors.role.toString() : ''} hasError={validateField('role')}>
            <Select
              data-cy="add-user-role-selector"
              options={currentEmployeeOrg?.type === EmployeeType.ADMIN ? UserRoleOptions : SiteRoleOptions}
              values={[values.role]}
              disabled={isLoading}
              onChange={(selected) => setFieldValue('role', selected?.[0])}
            />
          </FieldWrapper>

          {isSiteRole && (
            <>
              <FieldWrapper label="Site" message={validateField('site') ? errors.site.toString() : ''} hasError={validateField('site')}>
                <Select
                  data-cy="add-user-site-selector"
                  options={sites ? siteOptions : [{ label: '', value: '' }]}
                  values={[values.site]}
                  disabled={isLoading}
                  onChange={(selected) => {
                    const site = selected?.[0];
                    setFieldValue('site', site);
                    setFieldValue('disbursementMethod', getDefaultDisbursementMethod(site?.value, sitesWithSettings), false);
                  }}
                  onDropdownClose={() => setFieldTouched('site', true, false)}
                />
              </FieldWrapper>

              <FieldWrapper label="Job Title" message={validateField('jobTitle') ? errors.jobTitle.toString() : ''} hasError={validateField('jobTitle')}>
                <JobTitleCreatableSelect
                  dataCy="job-title-selector"
                  createNewLabel="Add {search} as a new job title"
                  defaultValue={values.jobTitle?.title}
                  disabled={!values.site?.value || isLoading}
                  placeholder="Select Job Title"
                  siteId={values.site?.value}
                  onChange={(selected) => setFieldValue('jobTitle', selected)}
                  onInputBlur={handleBlur('jobTitle')}
                />
              </FieldWrapper>

              <FieldWrapper data-cy="disbursement-field" label="Disbursement Method" hasError={validateField('disbursementMethod')}>
                <Select
                  data-cy="disbursement-input"
                  disabled={isLoading}
                  placeholder="Select Disbursement Method"
                  options={DisbursementOptions}
                  onChange={(selected) => {
                    setFieldValue('disbursementMethod', selected?.[0]);
                  }}
                  values={[values.disbursementMethod]}
                />
              </FieldWrapper>

              <FieldWrapper label="Employee ID" optional={!isEmployeeIdRequired(values.site?.value, sitesWithSettings)} message={validateField('employeeId') ? errors.employeeId : ''} hasError={validateField('employeeId')}>
                <Input data-cy="add-user-employee-id" placeholder="Employee ID" disabled={isLoading} maxLength={50} {...getFieldProps('employeeId')} />
              </FieldWrapper>
            </>
          )}
        </ModalFormLayout>
        <StyledErrorSection>{apiErrors?.errorMessages && apiErrors.errorMessages.map((error, index) => <FormError key={`error-message-${index}`}>{error}</FormError>)}</StyledErrorSection>

        <ModalFooter columns={2}>
          <Button label="Cancel" appearance="white" size="large" onClick={onClose} />
          <Button label="Add User" disabled={isLoading || !(isValid && dirty)} isProcessing={isLoading} type="submit" appearance="primary" size="large" />
        </ModalFooter>
      </ModalForm>
    </Modal>
  );
};

export default AddUserToSite;
